import React from 'react';
import {
  User as UserIcon,
  Building,
  CheckCircle2,
  AlertCircle,
  Check,
  Sparkles,
  Zap,
  TrendingDown,
  FileImage,
  X,
  Lock,
} from 'lucide-react';
import type { User } from '../../../../shared/types/index.js';
import { LazyAvatar } from '../../../../shared/components/index.js';

interface ProfileNameTabProps {
  user: User;
  displayName: string;
  setDisplayName: (name: string) => void;
  organization: string;
  setOrganization: (org: string) => void;
  nameSaving: boolean;
  nameSuccess: string | null;
  nameError: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onReset: () => void;
  onAvatarChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  uploadingAvatar: boolean;
  isConvertingAvatar?: boolean;
  avatarCompressionStats?: {
    originalSizeKB: number;
    compressedSizeKB: number;
    savingsPercentage: number;
  } | null;
  onDismissAvatarStats?: () => void;
}

export const ProfileNameTab: React.FC<ProfileNameTabProps> = ({
  user,
  displayName,
  setDisplayName,
  organization,
  setOrganization,
  nameSaving,
  nameSuccess,
  nameError,
  onSubmit,
  onReset,
  onAvatarChange,
  uploadingAvatar,
  isConvertingAvatar = false,
  avatarCompressionStats,
  onDismissAvatarStats,
}) => {
  return (
    <form onSubmit={onSubmit} className="space-y-6 animate-in fade-in duration-200 text-left">
      <div>
        <h3 className="text-base font-bold text-slate-900">Ubah Profil & Nama Tampilan</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Perbarui foto profil, nama tampilan, dan organisasi yang tampil di direktori publik UIBN.
        </p>
      </div>

      {nameSuccess && (
        <div className="p-3.5 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center gap-3 text-xs text-emerald-800">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{nameSuccess}</span>
        </div>
      )}

      {nameError && (
        <div className="p-3.5 rounded-2xl bg-rose-50 border border-rose-200 flex items-center gap-3 text-xs text-rose-800">
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
          <span>{nameError}</span>
        </div>
      )}

      {/* Profile Image / Avatar Upload */}
      <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-3">
        <label className="block text-xs font-bold text-slate-700">
          Foto Profil / Logo Publik ({user.role === 'penerbit' ? 'Penerbit' : 'Penulis'})
        </label>
        <p className="text-[11px] text-slate-500">
          Unggah foto profil atau logo institusi Anda. Gambar otomatis dikonversi ke format <strong>WebP</strong> sebelum diunggah ke Cloudflare R2 (file lama di bucket akan otomatis dibersihkan).
        </p>
        <div className="flex items-center gap-4">
          <LazyAvatar
            src={user.profileImageUrl}
            alt={user.name}
            size="xl"
            fallbackText={displayName.trim() || user.name}
            className="w-16 h-16 rounded-2xl border border-slate-200 shrink-0 shadow-2xs"
          />
          <div className="flex-1 space-y-2">
            <label className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50 transition shadow-2xs inline-flex items-center gap-2 cursor-pointer">
              {uploadingAvatar ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-indigo-600/30 border-t-indigo-600 rounded-full animate-spin" />
                  <span>Mengonversi WebP & Mengunggah...</span>
                </>
              ) : (
                <>
                  <UserIcon className="w-3.5 h-3.5 text-indigo-600" />
                  <span>{user.profileImageUrl ? 'Ganti Foto Profil (WebP)' : 'Unggah Foto Profil'}</span>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={onAvatarChange}
                disabled={uploadingAvatar}
                className="hidden"
              />
            </label>
            <p className="text-[10px] text-slate-400">
              Format: JPG, PNG, WEBP. Maks 5MB. Otomatis dikonversi ke WebP.
            </p>
          </div>
        </div>

        {/* WebP Conversion In-Progress Animation Banner for Avatar */}
        {uploadingAvatar && (
          <div className="mt-3 p-3 rounded-2xl bg-indigo-50/90 border border-indigo-200/80 text-indigo-900 animate-in fade-in zoom-in-95 duration-200 shadow-xs space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="relative flex items-center justify-center w-6 h-6 rounded-lg bg-indigo-600 text-white shadow-md shadow-indigo-600/30 shrink-0">
                  <Sparkles className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: '2.5s' }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-indigo-950 truncate">
                    Mengonversi Foto Profil ke WebP...
                  </p>
                  <p className="text-[10px] text-indigo-600 font-medium">
                    Mengompresi dan menyinkronkan ke Cloudflare R2
                  </p>
                </div>
              </div>
              <span className="shrink-0 px-2 py-0.5 rounded-full bg-indigo-200/70 text-indigo-800 text-[10px] font-extrabold tracking-wider uppercase animate-pulse">
                Memproses
              </span>
            </div>

            {/* Animated gradient progress bar */}
            <div className="w-full bg-indigo-200/60 h-1.5 rounded-full overflow-hidden relative">
              <div 
                className="absolute inset-0 bg-linear-to-r from-indigo-500 via-purple-500 to-indigo-600 rounded-full animate-pulse"
                style={{
                  backgroundSize: '200% 100%',
                }}
              />
            </div>
          </div>
        )}

        {/* WebP Conversion Result: Before & After File Size Display for Avatar */}
        {avatarCompressionStats && !uploadingAvatar && (
          <div className="relative mt-3 p-3.5 sm:p-4 rounded-2xl bg-emerald-50/90 border border-emerald-200/90 text-emerald-900 animate-in fade-in slide-in-from-top-1 duration-200 shadow-xs space-y-2.5">
            {/* Close button at top-right corner */}
            {onDismissAvatarStats && (
              <button
                type="button"
                onClick={onDismissAvatarStats}
                aria-label="Tutup info hasil konversi"
                className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 p-1 rounded-lg text-emerald-700 hover:text-emerald-950 hover:bg-emerald-200/60 transition cursor-pointer"
                title="Tutup kartu rincian"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            <div className="flex items-center justify-between flex-wrap gap-2 pr-7">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-emerald-600 text-white shadow-xs shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <span className="font-bold text-emerald-950 text-xs">
                  Foto Profil Berhasil Dikonversi ke WebP!
                </span>
              </div>
              {avatarCompressionStats.savingsPercentage > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-600 text-white font-black text-[10px] shadow-xs">
                  <TrendingDown className="w-3 h-3" />
                  <span>Hemat {avatarCompressionStats.savingsPercentage}% Ukuran</span>
                </span>
              )}
            </div>

            {/* Size Comparison Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
              <div className="p-2 rounded-xl bg-white/90 border border-emerald-100 flex flex-col justify-between">
                <span className="text-[10px] text-slate-500 font-medium flex items-center gap-1">
                  <FileImage className="w-3 h-3 text-slate-400" />
                  <span>Ukuran Asli:</span>
                </span>
                <span className="font-bold text-slate-700 text-xs mt-0.5">
                  {avatarCompressionStats.originalSizeKB} KB
                </span>
              </div>

              <div className="p-2 rounded-xl bg-emerald-100/60 border border-emerald-200 flex flex-col justify-between">
                <span className="text-[10px] text-emerald-700 font-medium flex items-center gap-1">
                  <Zap className="w-3 h-3 text-emerald-600" />
                  <span>Ukuran WebP:</span>
                </span>
                <span className="font-extrabold text-emerald-900 text-xs mt-0.5">
                  {avatarCompressionStats.compressedSizeKB} KB
                </span>
              </div>

              <div className="col-span-2 sm:col-span-1 p-2 rounded-xl bg-white/90 border border-emerald-100 flex flex-col justify-between">
                <span className="text-[10px] text-slate-500 font-medium">Format Output:</span>
                <span className="font-bold text-indigo-600 text-xs mt-0.5">
                  image/webp ⚡
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="input-profile-name" className="block text-xs font-bold text-slate-700 mb-1.5">
            Nama Lengkap / Nama Pena / Nama Penerbit
          </label>
          <div className="relative">
            <UserIcon className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              id="input-profile-name"
              type="text"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Contoh: Ahmad Fauzan, M.Hum"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition bg-white"
            />
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            Minimal 2 karakter. Perubahan nama akan otomatis memperbarui nama pada buku yang Anda tulis/terbitkan.
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="input-profile-org" className="block text-xs font-bold text-slate-700">
              {user.role === 'organisasi'
                ? 'Nama Organisasi / Komunitas'
                : user.role === 'penerbit'
                ? 'Nama Lembaga Penerbit Resmi'
                : 'Organisasi / Lembaga Terdaftar'}
            </label>
            {(user.role === 'penerbit' || user.role === 'organisasi') && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md">
                <Lock className="w-3 h-3 text-amber-600" />
                <span>Terkunci</span>
              </span>
            )}
            {user.role === 'penulis' && (
              <span className="text-[10px] text-slate-400 font-medium bg-slate-100 px-2 py-0.5 rounded">
                Dinonaktifkan
              </span>
            )}
          </div>
          <div className="relative">
            {user.role === 'penerbit' || user.role === 'organisasi' ? (
              <Lock className="w-4 h-4 text-amber-600 absolute left-3.5 top-1/2 -translate-y-1/2" />
            ) : (
              <Building className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            )}
            <input
              id="input-profile-org"
              type="text"
              readOnly={user.role === 'penerbit' || user.role === 'organisasi'}
              disabled={user.role === 'penulis'}
              value={user.role === 'penulis' ? '' : organization}
              onChange={(e) => setOrganization(e.target.value)}
              placeholder={
                user.role === 'penulis'
                  ? 'Dinonaktifkan untuk Penulis Mandiri'
                  : 'Nama terdaftar'
              }
              className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm transition ${
                user.role === 'penerbit' || user.role === 'organisasi'
                  ? 'bg-slate-100/90 text-slate-800 border-slate-200 font-semibold cursor-not-allowed select-none'
                  : user.role === 'penulis'
                  ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed select-none'
                  : 'border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 bg-white'
              }`}
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-1 leading-snug">
            {user.role === 'penerbit' || user.role === 'organisasi'
              ? 'Nama penerbit / organisasi/komunitas dikunci dan tidak bisa diupdate di profil untuk menjamin keaslian data terverifikasi.'
              : user.role === 'penulis'
              ? 'Akun Penulis Mandiri tidak terafiliasi lembaga/organisasi.'
              : 'Perubahan nama organisasi akan disinkronkan ke akun Anda.'}
          </p>
        </div>
      </div>

      {/* Live Preview Card */}
      <div className="p-4 rounded-2xl bg-indigo-50/50 border border-indigo-100 space-y-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-900 block">
          Pratinjau Tampilan Sertifikat & Katalog:
        </span>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white font-bold text-sm flex items-center justify-center shadow-2xs">
            {(displayName.trim() || user.name).charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-xs font-bold text-slate-900">
              {displayName.trim() || user.name}
            </p>
            <p className="text-[11px] text-slate-500">
              {organization.trim() || user.organization || 'Penulis Mandiri'} &bull;{' '}
              <span className="capitalize font-medium">{user.role}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={onReset}
          disabled={nameSaving}
          className="px-4 py-2 rounded-xl text-xs font-medium text-slate-600 hover:bg-slate-100 transition cursor-pointer disabled:opacity-50"
        >
          Reset
        </button>
        <button
          id="btn-save-profile-name"
          type="submit"
          disabled={nameSaving || !displayName.trim()}
          className="px-5 py-2.5 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs hover:shadow transition flex items-center gap-2 disabled:opacity-50 cursor-pointer"
        >
          {nameSaving ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Menyimpan...</span>
            </>
          ) : (
            <>
              <Check className="w-3.5 h-3.5" />
              <span>Simpan Perubahan Nama</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
};
